import { useMemo, useState } from "react";
import {
  distinctFinishChannels,
  filterManufacturingMaterialUses,
  type MaterialUsesActiveFilter,
  type MaterialUsesChannelFilter,
} from "@/lib/manufacturing/filterManufacturingMaterialUses";
import type { MfgMaterialUse } from "@/lib/manufacturing/types";
import { useManufacturingModelsListPagination } from "./useManufacturingModelsListPagination";

export function useManufacturingMaterialUsesListView(
  uses: readonly MfgMaterialUse[],
  memberSearchByUseId: Readonly<Record<string, string>>,
) {
  const [search, setSearchState] = useState("");
  const [activeFilter, setActiveFilterState] = useState<MaterialUsesActiveFilter>("active");
  const [channelFilter, setChannelFilterState] = useState<MaterialUsesChannelFilter>("all");

  const channels = useMemo(() => distinctFinishChannels(uses), [uses]);

  const filtered = useMemo(
    () =>
      filterManufacturingMaterialUses(uses, {
        search,
        activeFilter,
        channelFilter,
        memberSearchByUseId,
      }),
    [uses, search, activeFilter, channelFilter, memberSearchByUseId],
  );
  const pager = useManufacturingModelsListPagination(filtered);

  function setSearch(next: string) {
    setSearchState(next);
    pager.setPage(0);
  }

  function setActiveFilter(next: MaterialUsesActiveFilter) {
    setActiveFilterState(next);
    pager.setPage(0);
  }

  function setChannelFilter(next: MaterialUsesChannelFilter) {
    setChannelFilterState(next);
    pager.setPage(0);
  }

  return {
    search,
    setSearch,
    activeFilter,
    setActiveFilter,
    channelFilter,
    setChannelFilter,
    channels,
    filtered,
    filteredCount: filtered.length,
    page: pager.page,
    pageSize: pager.pageSize,
    pageCount: pager.pageCount,
    rangeStart: pager.rangeStart,
    rangeEnd: pager.rangeEnd,
    pageItems: pager.pageItems,
    setPage: pager.setPage,
    setPageSize: pager.setPageSize,
  };
}

export type { MaterialUsesActiveFilter, MaterialUsesChannelFilter };
